import { PerformanceUtilService } from './PerformanceUtil';

import PerformanceConsoleLog from './PerformanceConsoleLog';

export class ResourceMonitorService {
  timers = [];

  intervals = [];

  mutationObservers = [];

  performanceObservers = [];

  timeOutInterval = 500;

  timeOutMaxAttempts = 120;

  performanceUtilService = new PerformanceUtilService();

  monitor(
    mappingInfo,
    callback,
    startTimestamp = 0,
    intervalMs = this.timeOutInterval,
    maxAttempts = this.timeOutMaxAttempts,
  ) {
    if (mappingInfo.elementSelector) {
      this.monitorElement(mappingInfo.elementSelector, callback);
    } else if (mappingInfo.requestRegex) {
      this.monitorRequest(
        mappingInfo.requestRegex,
        callback,
        startTimestamp,
        intervalMs,
        maxAttempts,
      );
    } else if (mappingInfo.loadedFunc) {
      this.monitorLoadedFunc(
        mappingInfo.loadedFunc,
        callback,
        intervalMs,
        maxAttempts,
      );
    }
  }

  monitorElement(selector, callback) {
    try {
      if (typeof selector === 'string') {
        const map = new Map();
        map.set(selector, []);
        this.monitorElementSelectors(map, callback);
      } else if (selector instanceof Map) {
        this.monitorElementSelectors(selector, callback);
      } else {
        const map = new Map();
        const keys = Object.keys(selector);
        for (let i = 0; i < keys.length; i += 1) {
          const key = keys[i];
          const value = selector[key];
          map.set(key, value);
        }
        this.monitorElementSelectors(map, callback);
      }
    } catch (e) {
      console.error('Error in monitorElement:', e);
    }
  }

  monitorElementSelectors(selectors, callback) {
    const perfObserver = new MutationObserver((mutationsList, observer) => {
      try {
        mutationsList.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            const addedNode = mutation.addedNodes[0];
            selectors.forEach(([selectorKey, customFields]) => {
              if (
                addedNode.nodeType === Node.ELEMENT_NODE &&
                (addedNode.matches(selectorKey) ||
                  addedNode.querySelector(selectorKey))
              ) {
                PerformanceConsoleLog.logInfo(
                  'element found by observer',
                  selectorKey,
                );
                observer.disconnect();
                if (customFields && customFields.length > 0) {
                  callback(customFields);
                  return;
                }
                callback([]);
              }
            });
          } else if (mutation.type === 'attributes') {
            const targetNode = mutation.target;
            selectors.forEach(([selectorKey, customFields]) => {
              if (targetNode.matches(selectorKey)) {
                PerformanceConsoleLog.logInfo(
                  'element found by observer[attributes]',
                  selectorKey,
                );
                observer.disconnect();
                if (customFields && customFields.length > 0) {
                  callback(customFields);
                  return;
                }
                callback([]);
              }
            });
          }
        });
      } catch (e) {
        console.error('Error in MutationObserver:', e);
      }
    });

    this.mutationObservers.push(perfObserver);

    perfObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    const handleTimeout = () => {
      PerformanceConsoleLog.logInfo('observer.disconnect');
      perfObserver.disconnect();
    };

    const timeoutDuration = this.timeOutInterval * this.timeOutMaxAttempts;
    const timer = setTimeout(handleTimeout, timeoutDuration);
    this.timers.push(timer);
  }

  monitorRequest(
    selector,
    callback,
    startTimestamp = 0,
    intervalMs = this.timeOutInterval,
    maxAttempts = this.timeOutMaxAttempts,
  ) {
    try {
      const perfObserver = (list, observer) => {
        try {
          list.getEntries().forEach(entry => {
            if (
              selector.test(entry.name) &&
              'startTime' in entry &&
              entry.startTime >= startTimestamp
            ) {
              PerformanceConsoleLog.logInfo(
                'request found by oberserver',
                selector,
                entry.name,
              );
              observer.disconnect();
              callback([]);
            }
          });
        } catch (e) {
          PerformanceConsoleLog.logError('perfObserver error', e);
        }
      };

      const observer = new PerformanceObserver(perfObserver);
      observer.observe({ type: 'resource' });
      observer.observe({ type: 'navigation' });

      this.performanceObservers.push(observer);

      const handleTimeout = () => {
        PerformanceConsoleLog.logInfo('observer disconnect');
        observer.disconnect();
      };

      const timeoutDuration = intervalMs * maxAttempts;
      const timer = setTimeout(handleTimeout, timeoutDuration);
      this.timers.push(timer);
    } catch (e) {
      console.error('Error in monitorRequest:', e);
    }
  }

  monitorLoadedFunc(
    loadedFunc,
    callback,
    intervalMs = this.timeOutInterval,
    maxAttempts = this.timeOutMaxAttempts,
  ) {
    try {
      let attempts = 0;

      const interval = setInterval(() => {
        const loadedResult = loadedFunc();
        if (this.performanceUtilService.isBoolean(loadedResult)) {
          if (loadedResult) {
            clearInterval(interval);
            callback();
            return;
          }
        } else if (this.performanceUtilService.isArray(loadedResult)) {
          if (loadedResult.length > 0) {
            clearInterval(interval);
            callback(loadedResult);
            return;
          }
        }

        attempts += 1;
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          PerformanceConsoleLog.logInfo(
            'cannot be found in the specified time interval',
          );
        }
      }, intervalMs);

      this.intervals.push(interval);
    } catch (e) {
      console.error('Error in monitorLoadedFunc:', e);
    }
  }

  clearMonitors() {
    this.timers.forEach(timer => clearTimeout(timer));
    this.intervals.forEach(interval => clearInterval(interval));
    this.mutationObservers.forEach(observer => observer.disconnect());
    this.performanceObservers.forEach(observer => observer.disconnect());
    PerformanceConsoleLog.logInfo(
      `cleared: ${this.timers.length} timers, ${this.intervals.length} intervals, ${this.mutationObservers.length} mutationObservers, ${this.performanceObservers.length} performanceObservers`,
    );
    this.timers = [];
    this.intervals = [];
    this.mutationObservers = [];
    this.performanceObservers = [];
  }
}
